import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import Card, { CardHeader, CardContent } from "@/components/shared/ui/Card";
import { ExternalLink, X } from "lucide-react";

interface Exemplar {
  id: string;
  name: string;
  url?: string;
  key_locations?: string;
  funding_rounds?: string;
  key_investors?: string;
  genre?: string;
  niche_label?: string;
  crunchbase_link?: string;
  logo_url?: string;
  parent_company?: string;
  sites_count?: number;
  press_articles?: Array<{ title: string; url: string }>;
}

interface ProjectExemplar {
  what_we_like_dislike: string;
  display_order: number;
  exemplars: Exemplar;
}

interface ExemplarsWidgetProps {
  projectId: string;
}

export default function ExemplarsWidget({ projectId }: ExemplarsWidgetProps) {
  const [exemplars, setExemplars] = useState<ProjectExemplar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedExemplar, setSelectedExemplar] =
    useState<ProjectExemplar | null>(null);

  useEffect(() => {
    fetchExemplars();
  }, [projectId]);

  const fetchExemplars = async () => {
    try {
      setLoading(true);

      const { data, error: fetchError } = await supabase
        .from("project_exemplars")
        .select(
          `
          what_we_like_dislike,
          display_order,
          exemplars (
            id,
            name,
            url,
            key_locations,
            funding_rounds,
            key_investors,
            genre,
            niche_label,
            crunchbase_link,
            logo_url,
            parent_company,
            sites_count,
            press_articles
          )
        `
        )
        .eq("project_id", projectId)
        .order("display_order", { ascending: true });

      if (fetchError) throw fetchError;

      const transformedData = (data || [])
        .map((item: any) => ({
          what_we_like_dislike: item.what_we_like_dislike,
          display_order: item.display_order,
          exemplars: Array.isArray(item.exemplars)
            ? item.exemplars[0]
            : item.exemplars,
        }))
        .filter((item: any) => item.exemplars);

      setExemplars(transformedData);
    } catch (err) {
      console.error("Error fetching exemplars:", err);
      setError("Failed to load exemplars");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card variant="bordered">
        <CardHeader color="yellow">
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-black">
              Exemplars
            </h3>
            <p className="text-xs sm:text-sm text-gray-700 mt-1">
              A collection of brands and business we take inspiration and
              standards from
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 sm:py-8 text-gray-500 text-sm sm:text-base">
            Loading exemplars...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || exemplars.length === 0) {
    return null;
  }

  return (
    <>
      <Card variant="bordered">
        <CardHeader color="yellow">
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-black">
              Exemplars
            </h3>
            <p className="text-xs sm:text-sm text-gray-700 mt-1">
              A collection of brands and business we take inspiration and
              standards from
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {exemplars.map((item) => {
              const exemplar = item.exemplars;
              return (
                <div
                  key={exemplar.id}
                  onClick={() => setSelectedExemplar(item)}
                  className="border-2 border-black rounded-lg p-4 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-base sm:text-lg font-bold text-black mb-2">
                        {exemplar.name}
                      </h4>
                      {exemplar.niche_label && (
                        <div className="mb-2">
                          <span className="inline-block px-2 py-1 bg-yellow-400 text-black text-xs font-bold border-2 border-black">
                            {exemplar.niche_label}
                          </span>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-600">
                        {exemplar.key_locations && (
                          <span>📍 {exemplar.key_locations}</span>
                        )}
                        {exemplar.genre && <span>🏷️ {exemplar.genre}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Modal */}
      {selectedExemplar && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedExemplar(null)}
        >
          <div
            className="bg-white rounded-lg border-4 border-black max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-yellow-200 border-b-4 border-black p-4 sm:p-6">
              <div className="flex items-start gap-4">
                {selectedExemplar.exemplars.logo_url && (
                  <img
                    src={selectedExemplar.exemplars.logo_url}
                    alt={`${selectedExemplar.exemplars.name} logo`}
                    className="w-16 h-16 object-contain rounded border-2 border-black bg-white"
                  />
                )}
                <div className="flex-1">
                  <h2 className="text-xl sm:text-2xl font-bold text-black mb-2">
                    {selectedExemplar.exemplars.name}
                  </h2>
                  {selectedExemplar.exemplars.parent_company && (
                    <div className="text-sm text-gray-700 mb-2">
                      Part of {selectedExemplar.exemplars.parent_company}
                    </div>
                  )}
                  {selectedExemplar.exemplars.url && (
                    <a
                      href={selectedExemplar.exemplars.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-black hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="w-4 h-4" />
                      Visit Website
                    </a>
                  )}
                </div>
                <button
                  onClick={() => setSelectedExemplar(null)}
                  className="flex-shrink-0 p-2 hover:bg-black hover:bg-opacity-10 rounded transition-colors"
                  aria-label="Close"
                >
                  <X className="w-6 h-6 text-black" />
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {selectedExemplar.exemplars.key_locations && (
                  <div>
                    <div className="text-xs font-bold text-gray-600 uppercase mb-1">
                      Key Locations
                    </div>
                    <div className="text-sm sm:text-base text-black">
                      {selectedExemplar.exemplars.key_locations}
                    </div>
                  </div>
                )}
                {selectedExemplar.exemplars.genre && (
                  <div>
                    <div className="text-xs font-bold text-gray-600 uppercase mb-1">
                      Genre
                    </div>
                    <div className="text-sm sm:text-base text-black">
                      {selectedExemplar.exemplars.genre}
                    </div>
                  </div>
                )}
                {selectedExemplar.exemplars.sites_count !== undefined && (
                  <div>
                    <div className="text-xs font-bold text-gray-600 uppercase mb-1">
                      Number of Sites
                    </div>
                    <div className="text-sm sm:text-base text-black">
                      {selectedExemplar.exemplars.sites_count}
                    </div>
                  </div>
                )}
                {selectedExemplar.exemplars.funding_rounds && (
                  <div>
                    <div className="text-xs font-bold text-gray-600 uppercase mb-1">
                      Funding Rounds
                    </div>
                    <div className="text-sm sm:text-base text-black">
                      {selectedExemplar.exemplars.funding_rounds}
                    </div>
                  </div>
                )}
                {selectedExemplar.exemplars.key_investors && (
                  <div className="sm:col-span-2">
                    <div className="text-xs font-bold text-gray-600 uppercase mb-1">
                      Key Investors
                    </div>
                    <div className="text-sm sm:text-base text-black">
                      {selectedExemplar.exemplars.key_investors}
                    </div>
                  </div>
                )}
              </div>

              {(selectedExemplar.exemplars.crunchbase_link ||
                (Array.isArray(selectedExemplar.exemplars.press_articles) &&
                  selectedExemplar.exemplars.press_articles.length > 0)) && (
                <div className="pt-4 border-t-2 border-gray-200">
                  <div className="text-xs font-bold text-gray-600 uppercase mb-3">
                    External Resources
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedExemplar.exemplars.crunchbase_link && (
                      <a
                        href={selectedExemplar.exemplars.crunchbase_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white text-sm font-bold rounded-lg border-2 border-black hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Crunchbase
                      </a>
                    )}
                    {Array.isArray(selectedExemplar.exemplars.press_articles) &&
                      selectedExemplar.exemplars.press_articles.map(
                        (article, index) => (
                          <a
                            key={index}
                            href={article.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white text-sm font-bold rounded-lg border-2 border-black hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
                          >
                            <ExternalLink className="w-4 h-4" />
                            {article.title}
                          </a>
                        )
                      )}
                  </div>
                </div>
              )}

              <div className="pt-4 border-t-4 border-black">
                <div className="text-sm font-bold text-black mb-2 uppercase">
                  What We Like and Dislike
                </div>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  {selectedExemplar.what_we_like_dislike}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
